import type { CountryCode } from 'libphonenumber-js';
import { get } from 'lodash';
import { type ClipboardEvent, useRef, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import {
  type ContactDetailError,
  type ContactDetailHint,
  type ContactDetailRowValue,
  contactDetailHints,
  contactDetailKinds,
  formatPastedPhone,
  nextContactDetailType,
  phoneCountryAfterEdit,
  platformAfterEdit,
  platformWhenLeft,
  splitPastedValues
} from 'v5/core/contact-details/contactDetailKinds';
import type { FieldSpecOf } from 'v5/core/entity-schema/entitySchema.types';
import type { ContactDetailItem } from 'v5/core/entity-values/values.types';
import { type RowId, rowIdUtils } from 'v5/core/form-schema/formRows';

/** What shows under a row, most pressing first. */
export type ContactDetailRowNote =
  /** Blocks Save: what the row's kind rejects, or a row left blank once Save was tried. */
  | {
      type: 'error';
      code: ContactDetailError['code'] | 'value-required';
      part: 'type' | 'value';
    }
  /** A phone paste got the dial code; the paste as it was is one click away (⌘Z too). */
  | { type: 'pasted'; asPasted: string }
  | { type: 'split'; parts: string[] }
  | { type: 'hint'; hint: ContactDetailHint };

export type ContactDetailRowState = {
  key: string;
  row: ContactDetailItem;
  note: ContactDetailRowNote | null;
  /** The note stopped one Enter already; the next one goes on. */
  acknowledged: boolean;
};

type PendingPaste = {
  rowKey: string;
  /** The text the input would hold had the paste gone in untouched. */
  asPasted: string;
  /** Several values in one paste: offer a split. */
  parts: string[] | null;
};

function toRowValue(row: ContactDetailItem): ContactDetailRowValue {
  return { value: row.value, type: row.type, country: row.country ?? null };
}

function withCountry(
  item: Omit<ContactDetailItem, 'country'>,
  country: CountryCode | null
): ContactDetailItem {
  return country === null ? item : { ...item, country };
}

/**
 * The rows of a contact-details field in edit mode: the values in the form
 * (saved exactly as typed), plus what the rows say about them — hints, a
 * blocking error once the person tried to leave the row, a paste that was
 * reformatted or holds several values. The empty row at the end is not in
 * the form; its first input adds a row under `placeholderRowId`.
 */
export function useContactDetailRows(
  spec: FieldSpecOf<'contact-details'>,
  path: string,
  defaultCountry: CountryCode | null
) {
  const form = useFormContext<Record<string, ContactDetailItem[]>>();
  const rows = useWatch({ control: form.control, name: path }) ?? [];
  const { errors, submitCount } = useFormState({
    control: form.control,
    name: path
  });
  const [placeholderRowId, setPlaceholderRowId] = useState<RowId>(
    rowIdUtils.local
  );
  const [acknowledged, setAcknowledged] = useState<Record<string, string>>({});
  /**
   * The value each row was left with (focus moved out, or Enter): a hint
   * waits for it, so it doesn't nag while the value is being typed. The
   * values the editor opened with are left already.
   */
  const [settled, setSettled] = useState<Record<string, string>>(() =>
    Object.fromEntries(rows.map(row => [rowIdUtils.key(row.rowId), row.value]))
  );
  const [errorsShown, setErrorsShown] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [pastes, setPastes] = useState<
    Record<string, { value: string; asPasted: string; parts: string[] | null }>
  >({});
  const pendingPaste = useRef<PendingPaste | null>(null);

  const kind = contactDetailKinds[spec.detailType];
  const isPhone = spec.detailType === 'PHONE';
  const current = () => form.getValues(path) ?? [];
  const write = (next: ContactDetailItem[]) =>
    form.setValue(path, next, { shouldDirty: true, shouldValidate: true });
  const update = (rowKey: string, patch: Partial<ContactDetailItem>) =>
    write(
      current().map(row =>
        rowIdUtils.key(row.rowId) === rowKey ? { ...row, ...patch } : row
      )
    );
  const nextType = (used: ReadonlyArray<ContactDetailItem>) =>
    nextContactDetailType(
      spec.detailType,
      spec.typeOptions.map(option => option.value),
      used.map(row => row.type)
    );

  /** The paste that just went in: note it on the row it went into. */
  const notePaste = (rowKey: string, value: string) => {
    const pending = pendingPaste.current;
    pendingPaste.current = null;
    if (pending?.rowKey !== rowKey) return;
    if (pending.asPasted === value && pending.parts === null) return;
    setPastes(all => ({
      ...all,
      [rowKey]: { value, asPasted: pending.asPasted, parts: pending.parts }
    }));
  };

  const changeValue = (row: ContactDetailItem, value: string) => {
    const rowKey = rowIdUtils.key(row.rowId);
    notePaste(rowKey, value);
    const type = spec.platformRecognition
      ? platformAfterEdit(value, row.type)
      : row.type;
    write(
      current().map(item =>
        rowIdUtils.key(item.rowId) === rowKey
          ? withCountry(
              { rowId: row.rowId, value, type },
              isPhone ? phoneCountryAfterEdit(value, row.country ?? null) : null
            )
          : item
      )
    );
  };

  const startRow = (value: string) => {
    const rowKey = rowIdUtils.key(placeholderRowId);
    notePaste(rowKey, value);
    const used = current();
    const type = spec.platformRecognition
      ? platformAfterEdit(value, nextType(used))
      : nextType(used);
    write([
      ...used,
      withCountry(
        { rowId: placeholderRowId, value, type },
        isPhone ? phoneCountryAfterEdit(value, defaultCountry) : null
      )
    ]);
    setPlaceholderRowId(rowIdUtils.local());
  };

  /**
   * A paste into a row's value (or the empty row): a phone paste may get its
   * dial code; several values offer a split. `apply` takes the new value
   * where the browser can't insert it as an edit.
   */
  const onPaste = (
    rowKey: string,
    country: CountryCode | null,
    apply: (value: string) => void,
    event: ClipboardEvent<HTMLInputElement>
  ) => {
    const text = event.clipboardData.getData('text');
    const input = event.currentTarget;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const around = (inserted: string) =>
      input.value.slice(0, start) + inserted + input.value.slice(end);
    const parts = kind.splitsPaste ? splitPastedValues(text) : [];
    // Only a paste that is the whole number is read as one: into text
    // already there it goes as pasted.
    const replacesAll = start === 0 && end === input.value.length;
    const formatted =
      isPhone && replacesAll && parts.length < 2
        ? formatPastedPhone(text, country)
        : null;
    pendingPaste.current = {
      rowKey,
      asPasted: around(text),
      parts: parts.length > 1 ? parts : null
    };
    if (formatted === null || formatted === text.trim()) return;
    event.preventDefault();
    // Through the browser's own editing, so ⌘Z brings the paste back.
    const inserted =
      typeof document.execCommand === 'function' &&
      document.execCommand('insertText', false, formatted);
    if (!inserted) {
      apply(around(formatted));
    }
  };

  const hints = contactDetailHints(spec.detailType, rows.map(toRowValue));
  const noteOf = (
    row: ContactDetailItem,
    index: number,
    /** `settled`: only once the row was left with this value; `typed`: as typed. */
    hintWhen: 'settled' | 'typed'
  ): ContactDetailRowNote | null => {
    const key = rowIdUtils.key(row.rowId);
    // Problems wait until the row is left (or Enter, or Save is tried),
    // then follow the typing.
    const due =
      hintWhen === 'typed' || submitCount > 0 || settled[key] === row.value;
    const error = kind.error(toRowValue(row));
    // The list is one field: once any row was left, the form re-checks every
    // row on each keystroke. A row's own problems still wait for that row.
    const shown = due || errorsShown.has(key);
    const saveErrors = shown ? get(errors, `${path}.${index}`) : undefined;
    const paste = pastes[key];
    // A paste of several values stays an offer to split while it is
    // untouched: the error it shows is what the split resolves, and leaving
    // the row (for the button itself) must not take the button away.
    if (paste?.value === row.value && paste.parts !== null) {
      return { type: 'split', parts: paste.parts };
    }
    if (error !== null && shown) {
      return { type: 'error', code: error.code, part: error.part };
    }
    if (get(saveErrors, 'value') !== undefined && row.value.trim() === '') {
      return { type: 'error', code: 'value-required', part: 'value' };
    }
    if (paste?.value === row.value) {
      return { type: 'pasted', asPasted: paste.asPasted };
    }
    const hint = hints[index];
    if (hint === null || !due) return null;
    return { type: 'hint', hint };
  };
  const states: ContactDetailRowState[] = rows.map((row, index) => {
    const key = rowIdUtils.key(row.rowId);
    return {
      key,
      row,
      note: noteOf(row, index, 'settled'),
      acknowledged: acknowledged[key] === row.value
    };
  });

  return {
    states,
    placeholderKey: rowIdUtils.key(placeholderRowId),
    changeValue,
    startRow,
    onPaste,
    update,
    remove: (rowKey: string) =>
      write(current().filter(row => rowIdUtils.key(row.rowId) !== rowKey)),
    /** Fill this row with the first value and add the rest below as rows of their own. */
    split: (rowKey: string, parts: string[]) => {
      const [first, ...rest] = parts;
      const updated = current().map(row =>
        rowIdUtils.key(row.rowId) === rowKey ? { ...row, value: first } : row
      );
      const source = updated.find(row => rowIdUtils.key(row.rowId) === rowKey);
      write(
        rest.reduce<ContactDetailItem[]>(
          (all, value) => [
            ...all,
            withCountry(
              { rowId: rowIdUtils.local(), value, type: nextType(all) },
              isPhone
                ? phoneCountryAfterEdit(value, source?.country ?? null)
                : null
            )
          ],
          updated
        )
      );
    },
    rowFacts: (rowKey: string) => {
      const index = states.findIndex(s => s.key === rowKey);
      const state = states[index];
      const error = state ? kind.error(toRowValue(state.row)) : null;
      // Enter stops on a hint even before the row was left: that shows it.
      const note = state ? noteOf(state.row, index, 'typed') : null;
      return {
        blank: (state?.row.value.trim() ?? '') === '',
        error: error === null ? null : { part: error.part },
        unacknowledgedHint:
          note !== null && note.type !== 'error' && !state?.acknowledged
      };
    },
    acknowledge: (rowKey: string) => {
      const value = states.find(s => s.key === rowKey)?.row.value;
      if (value !== undefined) {
        setAcknowledged(all => ({ ...all, [rowKey]: value }));
        setSettled(all => ({ ...all, [rowKey]: value }));
      }
    },
    /** Focus left the row: its hint, if any, shows now; a handle may name its platform. */
    settle: (rowKey: string) => {
      const row = states.find(s => s.key === rowKey)?.row;
      if (row === undefined) return;
      if (settled[rowKey] !== row.value) {
        setSettled(all => ({ ...all, [rowKey]: row.value }));
      }
      if (spec.platformRecognition) {
        const type = platformWhenLeft(row.value, row.type);
        if (type !== row.type) update(rowKey, { type });
      }
    },
    showError: (rowKey: string) =>
      setErrorsShown(shown => new Set(shown).add(rowKey))
  };
}
